import cx from 'classnames';
import {
	GovDropdown,
	GovFormControl,
	GovIcon,
} from '@gov-design-system-ce/react';
import { FormErrorMessage, FormLabel } from '@gov-design-system-ce/react-form';
import type { DatovkaApiType } from '@gov-nx/api';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import {
	dataBoxLabel,
	getDataBoxIcon,
	getDataBoxTypeLabel,
	getDataBoxTypeShortcut,
	useDataBoxLabel,
	useDataBoxMessageFormContext,
} from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import { Button } from '@gov-nx/ui/web';

type DropdownItemProps = {
	dataBox: DatovkaApiType;
	selected?: boolean;
};

const DropdownItem = ({ dataBox, selected }: DropdownItemProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);

	return (
		<span className="whitespace-nowrap">
			<GovIcon
				name={getDataBoxIcon(dataBox.typSchranky)}
				type="templates"
				size="m"
				color="default"
			/>{' '}
			<span className="w-auto text-body-m">
				<strong
					className={cx(
						!selected && 'font-normal',
						'inline-flex overflow-hidden md:max-w-screen-sm xxl:max-w-[211px]',
						dataBox.typSchranky?.match(/^PFO/)
							? 'max-w-[158px] sm:max-w-[278px] ds:max-w-[148px] lg:max-w-[230px] xl:max-w-[146px]'
							: 'max-w-[239px] sm:max-w-[359px] ds:max-w-[229px] lg:max-w-[311px] xl:max-w-[227px]'
					)}>
					<span className="whitespace-nowrap overflow-hidden text-ellipsis text-(--text-primary)!">
						{dataBoxLabel(dataBox, true)}
					</span>
				</strong>
				<span
					className={cx(
						'text-(--text-tertiary)',
						selected && 'font-bold text-(--text-primary)!'
					)}>
					{' '}
					–{' '}
					<span className="xxl:hidden!">
						{getDataBoxTypeShortcut(dataBox.typSchranky, tn)}
					</span>
					<span className="hidden! xxl:inline!">
						{dataBox.typSchranky
							? getDataBoxTypeLabel(dataBox.typSchranky, tn)
							: null}
					</span>
				</span>
			</span>
		</span>
	);
};

export const SenderDropdown = () => {
	const { sender } = useDataBoxMessageFormContext();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);
	const { availableDataBoxes, isDropDownOpen, databox, onChange, error } =
		sender;
	const { label } = useDataBoxLabel();

	if (availableDataBoxes.length === 0) return null;

	return (
		<GovFormControl
			invalid={!!error}
			className="ds-new-message">
			<FormLabel
				isRequired={true}
				onClick={isDropDownOpen.setTrue}>
				{`${tn('nova-zprava.od')}:`}
			</FormLabel>
			{availableDataBoxes.length > 1 ? (
				<div className="relative w-full">
					<GovDropdown
						position="left"
						color="primary"
						type="base"
						size="m"
						aria-label={tn('nova-zprava.od.rozbalit')}
						open={isDropDownOpen.value}
						className="ds-new-message-dropdown"
						iconEnd={
							<GovIcon
								name="chevron-down"
								type="components"
								color="primary"
							/>
						}
						label={<DropdownItem dataBox={databox} />}>
						{availableDataBoxes.map((dataBox) => {
							const isSelected =
								dataBox.datovaSchrankaId === databox.datovaSchrankaId;

							return (
								<Button
									color="primary"
									type="base"
									size="m"
									isExpanded
									key={dataBox.datovaSchrankaId}
									className={cx(
										'whitespace-normal! border-(--border-subtlest)',
										isSelected
											? 'bg-(--background-primary-subtle)! hover:bg-(--button-outlined-primary-hover)! active:bg-(--button-outlined-primary-active)!'
											: ''
									)}
									onClick={() => onChange(dataBox.datovaSchrankaId as string)}
									aria-label={
										dataBoxLabel(dataBox, true) +
										' – ' +
										(dataBox.typSchranky ? label(dataBox.typSchranky) : null)
									}>
									<DropdownItem
										dataBox={dataBox}
										selected={isSelected}
									/>
								</Button>
							);
						})}
					</GovDropdown>
				</div>
			) : (
				<div className="ds-sender-text ml-3!">
					<DropdownItem dataBox={databox} />
				</div>
			)}
			{error && <FormErrorMessage message={error.message} />}
		</GovFormControl>
	);
};
